import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import {
  AlertTriangle,
  CheckCircle,
  Clock,
  Filter,
  Search,
  Eye,
  Download,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  RefreshCw,
  X,
  FileText,
  Plane,
  Building,
  Car,
  Utensils,
  Users,
  Calendar,
  Github,
  Copy,
  Check,
  MessageSquare,
  FileJson,
  Save
} from 'lucide-react';
import { Button } from '../ui/button';

// Error category display names
const ERROR_CATEGORY_LABELS = {
  date_time: 'Date/Time',
  location: 'Location',
  flight_details: 'Flight Details',
  hotel_details: 'Hotel Details',
  amounts: 'Amounts/Pricing',
  contact_info: 'Contact Info',
  missing_data: 'Missing Data',
  extra_data: 'Extra/Wrong Data',
  format: 'Format Issue',
  other: 'Other'
};

// Status configuration
const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  in_review: { label: 'In Review', color: 'bg-blue-100 text-blue-800', icon: Eye },
  resolved: { label: 'Resolved', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  wontfix: { label: "Won't Fix", color: 'bg-gray-100 text-gray-800', icon: X },
  needs_more_info: { label: 'Needs Info', color: 'bg-purple-100 text-purple-800', icon: AlertTriangle }
};

// Priority configuration
const PRIORITY_CONFIG = {
  low: { label: 'Low', color: 'bg-gray-100 text-gray-700' },
  medium: { label: 'Medium', color: 'bg-yellow-100 text-yellow-700' },
  high: { label: 'High', color: 'bg-orange-100 text-orange-700' },
  critical: { label: 'Critical', color: 'bg-red-100 text-red-700' }
};

// Component type icons
const COMPONENT_ICONS = {
  commercial_flight: Plane,
  private_flight: Plane,
  hotel: Building,
  rental_car: Car,
  restaurant: Utensils,
  meeting: Users,
  event: Calendar
};

const ParsingFeedbackAdmin = () => {
  const [feedbackList, setFeedbackList] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [componentTypeFilter, setComponentTypeFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Expanded items
  const [expandedId, setExpandedId] = useState(null);

  // Selected items for export
  const [selectedIds, setSelectedIds] = useState([]);

  // Send to Claude state
  const [sendingToClaude, setSendingToClaude] = useState(false);
  const [claudeExport, setClaudeExport] = useState(null);
  const [copied, setCopied] = useState(false);

  // Reviewer comments state
  const [reviewerComments, setReviewerComments] = useState({});
  const [savingComments, setSavingComments] = useState({});

  // System settings for export options
  const [exportSettings, setExportSettings] = useState({
    s3_export_enabled: true,
    github_commit_enabled: true
  });

  const apiBase = getApiBase();
  const headers = getAuthHeaders();

  const loadSystemSettings = async () => {
    try {
      const response = await axios.get(`${apiBase}/admin/system-settings`, { headers });
      if (response.data?.parsing_feedback) {
        setExportSettings(response.data.parsing_feedback);
      }
    } catch (err) {
      console.error('Error loading system settings:', err);
    }
  };

  const loadFeedback = async () => {
    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (priorityFilter) params.append('priority', priorityFilter);
      if (componentTypeFilter) params.append('component_type', componentTypeFilter);

      const response = await axios.get(
        `${apiBase}/parsing-feedback?${params.toString()}`,
        { headers }
      );

      // API returns { items: [...], total, limit, offset }
      setFeedbackList(response.data?.items || []);
    } catch (err) {
      console.error('Error loading parsing feedback:', err);
      setError('Failed to load parsing feedback');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(`${apiBase}/admin/parsing-feedback/stats`, { headers });
      setStats(response.data);
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  useEffect(() => {
    loadFeedback();
    loadStats();
    loadSystemSettings();
  }, [statusFilter, priorityFilter, componentTypeFilter]);

  const handleStatusChange = async (feedbackId, newStatus) => {
    try {
      await axios.put(
        `${apiBase}/parsing-feedback/${feedbackId}`,
        { status: newStatus },
        { headers }
      );
      loadFeedback();
      loadStats();
    } catch (err) {
      console.error('Error updating status:', err);
      setError('Failed to update status');
    }
  };

  const handleSaveReviewerComments = async (feedbackId) => {
    const comments = reviewerComments[feedbackId];
    if (comments === undefined) return;

    setSavingComments(prev => ({ ...prev, [feedbackId]: true }));
    try {
      await axios.put(
        `${apiBase}/parsing-feedback/${feedbackId}`,
        { reviewer_comments: comments },
        { headers }
      );
      // Update the local feedback list with new comments
      setFeedbackList(prev => prev.map(f =>
        f.id === feedbackId ? { ...f, reviewer_comments: comments } : f
      ));
    } catch (err) {
      console.error('Error saving reviewer comments:', err);
      setError('Failed to save reviewer comments');
    } finally {
      setSavingComments(prev => ({ ...prev, [feedbackId]: false }));
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.post(
        `${apiBase}/admin/parsing-feedback/export`,
        {
          feedback_ids: selectedIds.length > 0 ? selectedIds : undefined,
          status: statusFilter || undefined
        },
        { headers }
      );

      // Download as JSON file
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `parsing-feedback-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting feedback:', err);
      setError('Failed to export feedback');
    }
  };

  const handleSendToClaude = async () => {
    // Need at least one selected item
    const idsToSend = selectedIds.length > 0 ? selectedIds : feedbackList.filter(f => f.status === 'pending').map(f => f.id);

    if (idsToSend.length === 0) {
      setError('No feedback items selected. Select items or ensure there are pending items.');
      return;
    }

    setSendingToClaude(true);
    setError('');
    setClaudeExport(null);

    try {
      const response = await axios.post(
        `${apiBase}/admin/parsing-feedback/commit-to-github`,
        { feedback_ids: idsToSend },
        { headers }
      );

      setClaudeExport(response.data);
      loadFeedback(); // Refresh to show updated status
      loadStats();
    } catch (err) {
      console.error('Error sending to Claude:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to send feedback to GitHub';
      setError(errorMessage);
    } finally {
      setSendingToClaude(false);
    }
  };

  const copyExportToClipboard = async () => {
    if (!claudeExport) return;

    try {
      await navigator.clipboard.writeText(JSON.stringify(claudeExport, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const toggleSelection = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === feedbackList.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(feedbackList.map(f => f.id));
    }
  };

  const filteredFeedback = feedbackList.filter(item => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      item.user_description?.toLowerCase().includes(query) ||
      item.component_type?.toLowerCase().includes(query) ||
      item.organization_name?.toLowerCase().includes(query)
    );
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  if (loading && feedbackList.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <AlertTriangle className="w-6 h-6 text-amber-600" />
            Parsing Feedback
          </h2>
          <p className="text-gray-600">Review and manage document parsing issues reported by users</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            onClick={loadFeedback}
            variant="outline"
            className="flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
          {exportSettings.s3_export_enabled && (
            <Button
              onClick={handleExport}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Export (S3 URLs)
            </Button>
          )}
          {exportSettings.github_commit_enabled && (
            <Button
              onClick={handleSendToClaude}
              disabled={sendingToClaude}
              className="bg-purple-600 hover:bg-purple-700 text-white flex items-center gap-2"
            >
                {sendingToClaude ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Github className="w-4 h-4" />
              )}
              {sendingToClaude ? 'Sending...' : 'Send to Claude Code'}
            </Button>
          )}
        </div>
      </div>

      {/* Claude Export Result */}
      {claudeExport && (
        <div className="bg-purple-50 rounded-lg border border-purple-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-purple-600" />
              <span className="font-medium text-purple-900">
                Committed {claudeExport.case_count} case(s) to GitHub
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={copyExportToClipboard}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4 text-green-600" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    Copy for Claude
                  </>
                )}
              </Button>
              <Button
                onClick={() => setClaudeExport(null)}
                variant="ghost"
                size="sm"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <p className="text-sm text-purple-700 mb-2">
            Export ID: <code className="bg-purple-100 px-1 rounded">{claudeExport.export_id}</code>
          </p>
          <p className="text-sm text-purple-600">
            Click "Copy for Claude" and paste into Claude Code to process the feedback.
          </p>
          {claudeExport.errors && claudeExport.errors.length > 0 && (
            <div className="mt-2 text-sm text-red-600">
              {claudeExport.errors.length} error(s) occurred during commit.
            </div>
          )}
        </div>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-2xl font-bold text-gray-900">{stats.total || 0}</div>
            <div className="text-sm text-gray-500">Total</div>
          </div>
          <div className="bg-yellow-50 rounded-lg border border-yellow-200 p-4">
            <div className="text-2xl font-bold text-yellow-700">{stats.by_status?.pending || 0}</div>
            <div className="text-sm text-yellow-600">Pending</div>
          </div>
          <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
            <div className="text-2xl font-bold text-blue-700">{stats.by_status?.in_review || 0}</div>
            <div className="text-sm text-blue-600">In Review</div>
          </div>
          <div className="bg-green-50 rounded-lg border border-green-200 p-4">
            <div className="text-2xl font-bold text-green-700">{stats.by_status?.resolved || 0}</div>
            <div className="text-sm text-green-600">Resolved</div>
          </div>
          <div className="bg-red-50 rounded-lg border border-red-200 p-4">
            <div className="text-2xl font-bold text-red-700">{stats.by_priority?.critical || 0}</div>
            <div className="text-sm text-red-600">Critical</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Filters:</span>
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="text-sm border border-gray-300 rounded-md px-3 py-1.5 focus:ring-amber-500 focus:border-amber-500"
          >
            <option value="">All Statuses</option>
            {Object.entries(STATUS_CONFIG).map(([value, config]) => (
              <option key={value} value={value}>{config.label}</option>
            ))}
          </select>

          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            className="text-sm border border-gray-300 rounded-md px-3 py-1.5 focus:ring-amber-500 focus:border-amber-500"
          >
            <option value="">All Priorities</option>
            {Object.entries(PRIORITY_CONFIG).map(([value, config]) => (
              <option key={value} value={value}>{config.label}</option>
            ))}
          </select>

          <select
            value={componentTypeFilter}
            onChange={(e) => setComponentTypeFilter(e.target.value)}
            className="text-sm border border-gray-300 rounded-md px-3 py-1.5 focus:ring-amber-500 focus:border-amber-500"
          >
            <option value="">All Component Types</option>
            <option value="commercial_flight">Commercial Flight</option>
            <option value="private_flight">Private Flight</option>
            <option value="hotel">Hotel</option>
            <option value="rental_car">Rental Car</option>
            <option value="restaurant">Restaurant</option>
            <option value="meeting">Meeting</option>
            <option value="event">Event</option>
          </select>

          <div className="flex-1 max-w-xs">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search feedback..."
                className="w-full pl-9 pr-4 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-amber-500 focus:border-amber-500"
              />
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {error}
          <button onClick={() => setError('')} className="ml-2 text-red-500 hover:text-red-700">×</button>
        </div>
      )}

      {/* Feedback List */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {filteredFeedback.length === 0 ? (
          <div className="text-center py-12">
            <AlertTriangle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No feedback found</h3>
            <p className="text-gray-500">No parsing feedback matches your current filters</p>
          </div>
        ) : (
          <div>
            {/* Table Header */}
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center gap-4">
              <input
                type="checkbox"
                checked={selectedIds.length === feedbackList.length && feedbackList.length > 0}
                onChange={toggleSelectAll}
                className="h-4 w-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
              />
              <span className="text-xs font-medium text-gray-500 uppercase flex-1">
                {selectedIds.length > 0 ? `${selectedIds.length} selected` : `${filteredFeedback.length} items`}
              </span>
            </div>

            {/* Feedback Items */}
            {filteredFeedback.map((feedback) => {
              const isExpanded = expandedId === feedback.id;
              const statusConfig = STATUS_CONFIG[feedback.status] || STATUS_CONFIG.pending;
              const priorityConfig = PRIORITY_CONFIG[feedback.priority] || PRIORITY_CONFIG.medium;
              const ComponentIcon = COMPONENT_ICONS[feedback.component_type] || FileText;

              return (
                <div key={feedback.id} className="border-b border-gray-200 last:border-b-0">
                  {/* Summary Row */}
                  <div className="px-4 py-4 flex items-center gap-4 hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(feedback.id)}
                      onChange={() => toggleSelection(feedback.id)}
                      className="h-4 w-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                    />

                    <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                      <ComponentIcon className="w-5 h-5 text-amber-600" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusConfig.color}`}>
                          {statusConfig.label}
                        </span>
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${priorityConfig.color}`}>
                          {priorityConfig.label}
                        </span>
                        <span className="text-xs text-gray-500 capitalize">
                          {feedback.component_type?.replace(/_/g, ' ')}
                        </span>
                      </div>
                      <p className="text-sm text-gray-900 truncate">
                        {feedback.user_description || 'No description provided'}
                      </p>
                      <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                        <span>{feedback.organization_name || 'Unknown org'}</span>
                        <span>•</span>
                        <span>{formatDate(feedback.created_at)}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {/* Status dropdown */}
                      <select
                        value={feedback.status}
                        onChange={(e) => handleStatusChange(feedback.id, e.target.value)}
                        className="text-xs border border-gray-300 rounded px-2 py-1 focus:ring-amber-500 focus:border-amber-500"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {Object.entries(STATUS_CONFIG).map(([value, config]) => (
                          <option key={value} value={value}>{config.label}</option>
                        ))}
                      </select>

                      <button
                        onClick={() => setExpandedId(isExpanded ? null : feedback.id)}
                        className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
                      >
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5" />
                        ) : (
                          <ChevronDown className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="px-4 pb-4 pl-20 bg-gray-50">
                      <div className="grid md:grid-cols-2 gap-6">
                        {/* Left Column: Feedback Details */}
                        <div className="space-y-4">
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-2">Error Categories</h4>
                            <div className="flex flex-wrap gap-2">
                              {(feedback.error_categories || []).map(cat => (
                                <span key={cat} className="px-2 py-1 bg-amber-100 text-amber-800 text-xs rounded-full">
                                  {ERROR_CATEGORY_LABELS[cat] || cat}
                                </span>
                              ))}
                            </div>
                          </div>

                          {feedback.affected_fields?.length > 0 && (
                            <div>
                              <h4 className="text-sm font-medium text-gray-700 mb-2">Affected Fields</h4>
                              <div className="flex flex-wrap gap-2">
                                {feedback.affected_fields.map(field => (
                                  <span key={field} className="px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded-full">
                                    {field.replace(/_/g, ' ')}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-2">Description</h4>
                            <p className="text-sm text-gray-600 bg-white p-3 rounded-md border border-gray-200">
                              {feedback.user_description || 'No description provided'}
                            </p>
                          </div>

                          {feedback.expected_values && (
                            <div>
                              <h4 className="text-sm font-medium text-gray-700 mb-2">Expected Values</h4>
                              <pre className="text-xs bg-white p-3 rounded-md border border-gray-200 overflow-auto">
                                {typeof feedback.expected_values === 'object'
                                  ? JSON.stringify(feedback.expected_values, null, 2)
                                  : feedback.expected_values}
                              </pre>
                            </div>
                          )}
                        </div>

                        {/* Right Column: Artifacts & Actions */}
                        <div className="space-y-4">
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-2">Component Info</h4>
                            <div className="bg-white p-3 rounded-md border border-gray-200 text-sm">
                              <div className="grid grid-cols-2 gap-2">
                                <span className="text-gray-500">Type:</span>
                                <span className="capitalize">{feedback.component_type?.replace(/_/g, ' ')}</span>
                                <span className="text-gray-500">Component ID:</span>
                                <span className="font-mono text-xs">{feedback.component_id}</span>
                                {feedback.trip_id && (
                                  <>
                                    <span className="text-gray-500">Trip ID:</span>
                                    <span className="font-mono text-xs">{feedback.trip_id}</span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Documents Section */}
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-2">Documents for Export</h4>
                            <div className="bg-white p-3 rounded-md border border-gray-200 space-y-2">
                              {feedback.original_document && (
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <FileText className="w-4 h-4 text-gray-400" />
                                    <span className="text-sm">{feedback.original_document.filename || 'Original Document'}</span>
                                  </div>
                                  {feedback.original_document.presigned_url && (
                                    <a
                                      href={feedback.original_document.presigned_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-amber-600 hover:text-amber-700 flex items-center gap-1 text-sm"
                                    >
                                      <ExternalLink className="w-4 h-4" />
                                      View
                                    </a>
                                  )}
                                </div>
                              )}
                              {feedback.parsed_output_url && (
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <FileJson className="w-4 h-4 text-blue-400" />
                                    <span className="text-sm">Parsed Output (JSON)</span>
                                  </div>
                                  <a
                                    href={feedback.parsed_output_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-amber-600 hover:text-amber-700 flex items-center gap-1 text-sm"
                                  >
                                    <ExternalLink className="w-4 h-4" />
                                    View
                                  </a>
                                </div>
                              )}
                              {feedback.component_snapshot_url && (
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <FileJson className="w-4 h-4 text-green-400" />
                                    <span className="text-sm">Component Snapshot (JSON)</span>
                                  </div>
                                  <a
                                    href={feedback.component_snapshot_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-amber-600 hover:text-amber-700 flex items-center gap-1 text-sm"
                                  >
                                    <ExternalLink className="w-4 h-4" />
                                    View
                                  </a>
                                </div>
                              )}
                              {!feedback.original_document && !feedback.parsed_output_url && !feedback.component_snapshot_url && (
                                <p className="text-sm text-gray-500 italic">No documents available</p>
                              )}
                            </div>
                          </div>

                          {/* Reviewer Comments Section */}
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                              <MessageSquare className="w-4 h-4" />
                              Reviewer Comments
                            </h4>
                            <p className="text-xs text-gray-500 mb-2">
                              Add context for Claude Code analysis (included in export)
                            </p>
                            <div className="space-y-2">
                              <textarea
                                value={reviewerComments[feedback.id] ?? feedback.reviewer_comments ?? ''}
                                onChange={(e) => setReviewerComments(prev => ({
                                  ...prev,
                                  [feedback.id]: e.target.value
                                }))}
                                placeholder="Add comments about this parsing issue for Claude Code to consider..."
                                className="w-full h-24 text-sm border border-gray-300 rounded-md p-2 focus:ring-amber-500 focus:border-amber-500"
                              />
                              <Button
                                onClick={() => handleSaveReviewerComments(feedback.id)}
                                disabled={savingComments[feedback.id] || (reviewerComments[feedback.id] === undefined && !feedback.reviewer_comments)}
                                size="sm"
                                className="flex items-center gap-2"
                              >
                                {savingComments[feedback.id] ? (
                                  <RefreshCw className="w-3 h-3 animate-spin" />
                                ) : (
                                  <Save className="w-3 h-3" />
                                )}
                                {savingComments[feedback.id] ? 'Saving...' : 'Save Comments'}
                              </Button>
                            </div>
                          </div>

                          {feedback.admin_notes && (
                            <div>
                              <h4 className="text-sm font-medium text-gray-700 mb-2">Admin Notes</h4>
                              <p className="text-sm text-gray-600 bg-white p-3 rounded-md border border-gray-200">
                                {feedback.admin_notes}
                              </p>
                            </div>
                          )}

                          {feedback.resolution_notes && (
                            <div>
                              <h4 className="text-sm font-medium text-gray-700 mb-2">Resolution</h4>
                              <p className="text-sm text-gray-600 bg-white p-3 rounded-md border border-gray-200">
                                {feedback.resolution_notes}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ParsingFeedbackAdmin;
